import React from 'react';
import { Card, Row, Col, Alert, Progress, Typography, Button } from 'antd';
import { Link } from 'react-router-dom';
import { useOwnerFinancialHealth } from '@/hooks/useOwnerIntelligence';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  PrimaryKpiCard,
  OiPageFrame,
  OiMetricHelp,
  HeroKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import {
  ComparativeStatementTable,
  mergeComparativeLines,
} from '@/components/owner-intelligence/ComparativeStatementTable';
import { formatFiscalYearPeriodLabel, getFiscalYearRange } from '@/utils/fiscalYear.util';

const { Text } = Typography;

const FinancialHealthPage: React.FC = () => {
  const { data, isLoading, error } = useOwnerFinancialHealth(2);
  const { formatCurrency } = useCurrency();

  const periods = data?.periods ?? [];
  const periodLabels = periods.map((p) => {
    const range = getFiscalYearRange(data?.fiscal_year_start_month ?? 1, parseInt(p.fiscal_year_label.replace('FY', ''), 10));
    const effectiveEnd = p.effective_to_date !== p.to_date ? ` (to ${p.effective_to_date})` : '';
    return `${formatFiscalYearPeriodLabel(range)}${effectiveEnd}`;
  });

  const plRows = mergeComparativeLines(periods, 'pl_lines');
  const bsRows = mergeComparativeLines(periods, 'bs_lines');

  const mergedSources = periods.reduce<Record<string, import('@/components/owner-intelligence/ComparativeStatementTable').FinancialDataSource>>(
    (acc, p) => ({ ...acc, ...p.sources }),
    {}
  );
  const hasEstimates = Object.values(mergedSources).some((s) => s === 'estimate' || s === 'hybrid');
  const dataQualityNotes = [...new Set(periods.flatMap((p) => p.data_quality ?? []))];

  return (
    <OiPageFrame
      subtitle="Comparative P&L and balance sheet by fiscal year — ledger truth with estimate fallback."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Financial health data unavailable"
    >
      {data ? (
        <>
          {hasEstimates ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Hybrid data sources"
              description={
                <span>
                  Some figures use invoicing plus trading/rental and BOQ estimation costs where ledger data is incomplete. Rows tagged{' '}
                  <Text strong>Est.</Text> or <Text strong>Hybrid</Text> are not fully audited.{' '}
                  <Link to="/fin/reports">Open full financial reports</Link>
                </span>
              }
            />
          ) : null}

          {dataQualityNotes.length ? (
            <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={dataQualityNotes.join(' ')} />
          ) : null}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            {periods.map((p) => (
              <Col xs={24} md={12} key={p.fiscal_year_label}>
                <HeroKpiCard
                  label={p.fiscal_year_label}
                  value={
                    <div>
                      <div style={{ fontSize: 14, marginBottom: 8 }}>
                        Net Profit: {formatCurrency(p.net_profit)}
                      </div>
                      <div style={{ fontSize: 14 }}>Cash: {formatCurrency(p.cash_balance)}</div>
                    </div>
                  }
                  sublabel={`${p.from_date} – ${p.effective_to_date}`}
                />
              </Col>
            ))}
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="Net profit (current FY)"
                value={formatCurrency(periods[0]?.net_profit ?? 0)}
                deltaPct={data.yoy?.net_profit_pct}
              />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="Cash (current FY)"
                value={formatCurrency(periods[0]?.cash_balance ?? 0)}
                deltaPct={data.yoy?.cash_pct}
              />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="Revenue (current FY)"
                value={formatCurrency(periods[0]?.revenue_total ?? 0)}
                deltaPct={data.yoy?.revenue_pct}
              />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="Net margin %"
                value={
                  periods[0]?.net_profit_margin_pct != null
                    ? `${periods[0].net_profit_margin_pct.toFixed(1)}%`
                    : '—'
                }
              />
            </Col>
          </Row>

          <Card
            title={
              <span>
                Net margin by fiscal year{' '}
                <OiMetricHelp title="Net margin">
                  Net profit as a percentage of revenue for each fiscal year. Uses posted ledger where available.
                </OiMetricHelp>
              </span>
            }
            style={{ marginBottom: 16 }}
            bordered={false}
          >
            <Row gutter={24}>
              {periods.map((p) => (
                <Col xs={24} sm={12} key={`margin-${p.fiscal_year_label}`}>
                  <Text type="secondary">{p.fiscal_year_label}</Text>
                  <Progress
                    percent={Math.min(100, Math.max(0, p.net_profit_margin_pct ?? 0))}
                    format={(pct) => `${(pct ?? 0).toFixed(0)}%`}
                    strokeColor="var(--oi-signal-blue)"
                  />
                </Col>
              ))}
            </Row>
          </Card>

          <Card
            title={
              <span>
                Comparative Profit &amp; Loss{' '}
                <OiMetricHelp title="Comparative P&L">
                  Fiscal-year P&L from posted journal vouchers. Revenue and costs fall back to invoicing and estimation
                  loaded costs (trading/rental estimations + BOQ production estimations) when the general ledger has no
                  income activity.
                </OiMetricHelp>
              </span>
            }
            style={{ marginBottom: 16 }}
            bordered={false}
            extra={
              <Button type="link" size="small">
                <Link to="/fin/reports">Full reports</Link>
              </Button>
            }
          >
            <ComparativeStatementTable
              periodLabels={periodLabels}
              rows={plRows}
              sources={mergedSources}
              formatAmount={formatCurrency}
            />
          </Card>

          <Card
            title={
              <span>
                Balance Sheet{' '}
                <OiMetricHelp title="Balance sheet">
                  Point-in-time snapshot at fiscal period end from posted balances. Cash uses ledger cash-flow
                  closing balance where available.
                </OiMetricHelp>
              </span>
            }
            bordered={false}
          >
            <ComparativeStatementTable
              periodLabels={periodLabels}
              rows={bsRows}
              sources={mergedSources}
              formatAmount={formatCurrency}
              showPctOfRevenue={false}
            />
          </Card>
        </>
      ) : null}
    </OiPageFrame>
  );
};

export default FinancialHealthPage;
